import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, Phone, Mail, ArrowRight, Download } from 'lucide-react';

interface PaymentSuccess {
  paymentId: string;
  transactionId: string;
  amount: number;
}

const Success: React.FC = () => {
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState<PaymentSuccess | null>(null);

  useEffect(() => {
    const data = sessionStorage.getItem('paymentSuccess');
    if (data) {
      setPaymentData(JSON.parse(data));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleScheduleOnboarding = () => {
    // In a real app, this would integrate with a calendar system
    alert('Onboarding scheduling feature would be integrated here');
  };

  const handleDownloadReceipt = () => {
    // In a real app, this would generate and download a PDF receipt
    alert('Receipt download feature would be integrated here');
  };

  if (!paymentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading confirmation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-16">
        <div className="max-w-3xl mx-auto text-center">
          {/* Success Icon */}
          <div className="mb-8">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome to Grid Managed Services!
            </h1>
            <p className="text-xl text-gray-600">
              Your subscription has been successfully activated. We're excited to support your IT needs.
            </p>
          </div>

          {/* Payment Confirmation */}
          <div className="card mb-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Payment Confirmed</h2>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Amount Paid</div>
                  <div className="text-2xl font-bold text-green-600">${paymentData.amount}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Transaction ID</div>
                  <div className="font-mono text-sm">{paymentData.transactionId}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Payment ID</div>
                  <div className="font-mono text-sm">{paymentData.paymentId}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="card text-left">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Schedule Onboarding</h3>
                  <p className="text-gray-600 mb-4">
                    Book a 30-minute onboarding call with our technical team to get started.
                  </p>
                  <button 
                    onClick={handleScheduleOnboarding}
                    className="btn btn-primary"
                  >
                    Schedule Call
                  </button>
                </div>
              </div>
            </div>

            <div className="card text-left">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Download Receipt</h3>
                  <p className="text-gray-600 mb-4">
                    Get your payment receipt and service agreement for your records.
                  </p>
                  <button 
                    onClick={handleDownloadReceipt}
                    className="btn btn-outline"
                  >
                    Download Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="card text-left mb-8">
            <h3 className="text-xl font-bold mb-6 text-center">What Happens Next?</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Welcome Email</h4>
                  <p className="text-gray-600">
                    You'll receive a welcome email within the next few minutes with your account details and next steps.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Onboarding Call</h4>
                  <p className="text-gray-600">
                    Our technical team will contact you within 24 hours to schedule your onboarding session.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-semibold mb-1">System Assessment</h4>
                  <p className="text-gray-600">
                    We'll conduct a comprehensive assessment of your current IT infrastructure and security posture.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  4
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Service Activation</h4>
                  <p className="text-gray-600">
                    Your IT support services will be fully activated and monitoring will begin immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="card bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <Phone className="w-6 h-6 text-blue-600 mt-1" />
                <div className="text-left">
                  <h4 className="font-semibold text-blue-900 mb-2">Emergency Support</h4>
                  <p className="text-blue-800 text-sm mb-2">
                    24/7 emergency support is now active for critical issues.
                  </p>
                  <div className="font-semibold text-blue-900">(555) 123-HELP</div>
                </div>
              </div>
            </div>

            <div className="card bg-green-50 border-green-200">
              <div className="flex items-start gap-3">
                <Mail className="w-6 h-6 text-green-600 mt-1" />
                <div className="text-left">
                  <h4 className="font-semibold text-green-900 mb-2">General Support</h4>
                  <p className="text-green-800 text-sm mb-2">
                    For non-emergency support requests and questions.
                  </p>
                  <div className="font-semibold text-green-900">support@gridmanagedservices.com</div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary btn-lg flex items-center gap-2"
            >
              Access Client Portal
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => navigate('/')}
              className="btn btn-outline btn-lg"
            >
              Return to Home
            </button>
          </div>

          {/* Footer Note */}
          <div className="mt-12 p-6 bg-gray-100 rounded-lg">
            <p className="text-gray-600 text-sm">
              <strong>Important:</strong> Please save this confirmation page and your transaction ID for your records. 
              If you have any questions or need immediate assistance, don't hesitate to contact our support team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Success;